import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentLoaderService } from './environment-loader.service';
import { PagedResult } from '../models/paging';
import { Role, User, UserDepartmentResponse, UserSearchEvent } from 'src/app/features/users/user.model';

export interface UserCreateRequest {
  organizationId: number;
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
}

export interface UserEditRequest {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roles: Role[];
  rolesToRemove: Role[];
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject(EnvironmentLoaderService);

  apiUrl: string;
  apiUrlAutomation: string;

  constructor() {
    this.apiUrl = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/admin/user';
    this.apiUrlAutomation = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/automation/';
  }

  getUsers(searchEvent?: UserSearchEvent): Observable<PagedResult<User>> {
    const url = `${this.apiUrl}`;
    let params = new HttpParams();
    const paging = searchEvent?.paging;
    const sorting = searchEvent?.sorting;

    if (searchEvent?.organizationId) {
      params = params.append('organizationId', searchEvent.organizationId);
    }
    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (searchEvent?.roleName) {
      params = params.append('roleName', searchEvent.roleName);
    }
    if (searchEvent?.includeDisabled) {
      params = params.append('includeDisabled', searchEvent.includeDisabled);
    }
    if (paging?.pageIndex) {
      params = params.append('page', paging.pageIndex);
    }
    if (paging?.pageSize) {
      params = params.append('pageSize', paging.pageSize);
    }
    if (sorting?.active) {
      params = params.append('sortby', sorting.active);
    }
    if (sorting?.direction) {
      params = params.append('sortdirection', sorting.direction);
    }

    return this.http.get<PagedResult<User>>(url, {
      params,
    });
  }

  getUser(id: number): Observable<User> {
    const url = this.apiUrl + '/' + id;
    return this.http.get<User>(url);
  }

  getRoles(): Observable<Role[]> {
    const url = this.apiUrl + '/roles';
    return this.http.get<Role[]>(url);
  }

  syncUsers(): Observable<void> {
    const url = this.apiUrlAutomation + 'syncusers';
    return this.http.post<void>(url, null);
  }

  create(user: UserCreateRequest): Observable<number> {
    const url = this.apiUrl;

    return this.http.post<number>(url, {
      organizationId: user.organizationId,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: { name: user.role.name, id: user.role.id },
    });
  }

  edit(user: UserEditRequest): Observable<string> {
    const url = this.apiUrl + '/' + user.id;

    return this.http.put<string>(url, {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      roleToAdd: user.roles?.[0],
      roleToRemove: user.rolesToRemove?.[0],
    });
  }

  toggleActivationOnUser(organizationId: number, userId: number, disable: boolean) {
    let url = `${this.apiUrl}/${organizationId}/${userId}/`;
    if (disable) {
      url += 'true';
    } else {
      url += 'false';
    }
    return this.http.put<void>(url, null);
  }

  public restrictPortalUserDeparments({
    organizationId,
    userId,
    departmentIds,
    departmentNames,
  }: {
    organizationId: number;
    userId: number;
    departmentIds: number[];
    departmentNames: string[];
  }): Observable<number> {
    const url = `${this.apiUrl}/DepartmentRestrictions`;
    return this.http.post<number>(url, {
      organizationId: organizationId,
      portalUserId: userId,
      departmentIds: departmentIds,
      departmentNames: departmentNames,
    });
  }

  public getUserDepartments(id: number): Observable<UserDepartmentResponse[]> {
    const url = `${this.apiUrl}s/${id}/departments`;
    return this.http.get<UserDepartmentResponse[]>(url);
  }

  public resendInvitation(id: number): Observable<void> {
    const url = `${this.apiUrl}/${id}/resend-invitation`;
    return this.http.post<void>(url, null);
  }
}
